'use client'

import { InfoCircle24 } from '@vinted/monochrome-icons'
import {
  Button,
  Cell,
  Checkbox,
  Divider,
  Icon,
  Image,
  InputText,
  Spacer,
  Text,
} from '@vinted/web-ui'
import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { InformationModal, Heading } from '@marketplace-web/business-accounts/business-accounts-ui'
import { ErrorItem } from '@marketplace-web/core-api/api-client-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { createPhotoFormData } from '@marketplace-web/item-upload/media-upload-feature'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import {
  renderValidation,
  useFormValidationMessage,
} from '@marketplace-web/vendor-abstractions/react-hook-form-feature'
import { EMPTY_USER_IMAGE_NAME, useAsset } from '@marketplace-web/shared/assets'
import { createPhoto } from '@marketplace-web/item-upload/media-upload-data'
import {
  viewScreenEvent,
  BusinessRegistrationModel,
} from '@marketplace-web/business-accounts/business-accounts-data'

import { GeneralError } from '../GeneralError'
import { Section } from '../Section'
import { camelCase } from './utils'
import { findThumbnail } from '../../../utils/photo'
import { isValueInObject } from '../../../utils/object'

type Props = {
  defaultValues: BusinessRegistrationModel
  apiErrors: Array<ErrorItem> | null
  showGeneralError: boolean
}

enum Field {
  PictureId = 'profile.photoTempUuid',
  PictureUrl = 'profile.photoUrl',
  BusinessAccountName = 'profile.businessAccountName',
  ContactEmail = 'profile.email',
  ContactNumber = 'profile.phoneNumber',
  IsEmailPersonal = 'profile.isEmailPersonal',
  IsPhoneNumberPersonal = 'profile.isPhoneNumberPersonal',
}

const TRANSLATION_PREFIX = 'business.registration'

const BusinessProfile = ({ apiErrors, defaultValues, showGeneralError }: Props) => {
  const { track } = useTracking()
  const asset = useAsset('assets/kyc')
  const noPhotoAsset = useAsset('assets/no-photo')

  const translate = useTranslate(`${TRANSLATION_PREFIX}.profile`)
  const {
    register,
    setValue,
    reset,
    setError,
    watch,
    control,
    formState: { errors },
  } = useFormContext<Partial<BusinessRegistrationModel>>()
  const validationMessage = useFormValidationMessage(errors, TRANSLATION_PREFIX)
  const entityType = defaultValues.businessDetails?.entityType

  const [isPersonalContactInfoVisible, setIsPersonalContactInfoVisible] = useState(false)
  const [isEmailInfoClicked, setIsEmailInfoClicked] = useState(false)

  const photoFileInput = useRef<HTMLInputElement>(null)

  const pictureUrl = watch(Field.PictureUrl)

  useEffect(() => {
    track(
      viewScreenEvent({
        screen: 'registration_business_profile',
      }),
    )
  }, [track])

  useEffect(() => {
    apiErrors?.forEach(({ field, value }) => {
      const fieldName = camelCase(field)

      if (!isValueInObject(fieldName, Field)) return

      setError(fieldName, { type: 'manual', message: value })
    })
  }, [setError, apiErrors])

  useEffect(() => {
    if (!defaultValues.profile) return
    reset({ profile: defaultValues.profile })
  }, [reset, defaultValues])

  async function onFileInputChange(event: ChangeEvent<HTMLInputElement>) {
    if (!event.target.files?.length) return

    const file = event.target.files[0]!

    const formData = createPhotoFormData(file, 'user')
    const response = await createPhoto(formData)

    if ('errors' in response) return

    setValue(Field.PictureId, response.temp_uuid || undefined)
    setValue(Field.PictureUrl, findThumbnail(response))
  }

  function showPhotoFileUpload() {
    photoFileInput.current?.click()
  }

  function renderBusinessNameValidation() {
    const errorMessage = validationMessage(Field.BusinessAccountName)

    if (errorMessage) {
      return renderValidation(errorMessage)
    }

    return <Text as="h4" type={Text.Type.Caption} text={translate('business_account_name.hint')} />
  }

  function handleInfoIconClick() {
    setIsPersonalContactInfoVisible(true)
  }

  function handlePersonalContactInfoModalClose() {
    setIsPersonalContactInfoVisible(false)
  }

  function renderInformationModal() {
    const informationCategory = isEmailInfoClicked
      ? 'email_address_personal'
      : 'phone_number_personal'
    const labels = {
      title: translate(`profile_step.${informationCategory}.info.title`),
      description: translate(`profile_step.${informationCategory}.info.description`),
      closeButtonText: translate(`profile_step.${informationCategory}.info.button`),
    }

    return (
      <InformationModal
        {...labels}
        show={isPersonalContactInfoVisible}
        iconPath={asset('lamp.svg')}
        onClose={handlePersonalContactInfoModalClose}
      />
    )
  }

  function renderIsPhoneNumberPersonalTitle() {
    return (
      <span>
        <span>{translate('profile_step.phone_number_personal.check')}</span>
        <Button
          icon={
            <Icon
              name={InfoCircle24}
              color={Icon.Color.GreyscaleLevel2}
              aria={{ 'aria-hidden': 'true' }}
            />
          }
          styling={Button.Styling.Flat}
          inline
          size={Button.Size.Small}
          onClick={() => {
            setIsEmailInfoClicked(false)
            handleInfoIconClick()
          }}
        />
      </span>
    )
  }

  function renderIsEmailPersonalTitle() {
    return (
      <span>
        <span>{translate('profile_step.email_address_personal.check')}</span>
        <Button
          icon={
            <Icon
              name={InfoCircle24}
              color={Icon.Color.GreyscaleLevel2}
              aria={{ 'aria-hidden': 'true' }}
            />
          }
          styling={Button.Styling.Flat}
          inline
          size={Button.Size.Small}
          onClick={() => {
            setIsEmailInfoClicked(true)
            handleInfoIconClick()
          }}
        />
      </span>
    )
  }

  function renderIsPhoneNumberPersonalCheckbox() {
    return (
      <Controller
        name={Field.IsPhoneNumberPersonal}
        render={({ field: { name, value, onChange } }) => (
          <Checkbox
            name={name}
            text={renderIsPhoneNumberPersonalTitle()}
            testId="dsa_profile_phone_number_personal"
            checked={value}
            onChange={event => onChange(event.target.checked)}
          />
        )}
        control={control}
      />
    )
  }

  function renderIsEmailPersonalCheckbox() {
    return (
      <Controller
        name={Field.IsEmailPersonal}
        render={({ field: { name, value, onChange } }) => (
          <Checkbox
            name={name}
            text={renderIsEmailPersonalTitle()}
            testId="dsa_profile_email_personal"
            checked={!!value}
            onChange={event => onChange(event.target.checked)}
          />
        )}
        control={control}
      />
    )
  }

  return (
    <SeparatedList separator={<Spacer size={Spacer.Size.X2Large} />}>
      <Heading
        styling={Cell.Styling.Tight}
        title={translate('title')}
        subtitle={translate('subtitle')}
      />
      {showGeneralError && <GeneralError />}
      <Section>
        <SeparatedList separator={<Divider />}>
          <>
            <Cell
              styling={Cell.Styling.Wide}
              title={translate('photo_temp_uuid.title')}
              suffix={
                <div className="u-flexbox u-align-items-center">
                  <SeparatedList separator={<Spacer size={Spacer.Size.XLarge} />}>
                    <div>
                      <Image
                        role="img"
                        src={pictureUrl || noPhotoAsset(EMPTY_USER_IMAGE_NAME)}
                        size={Image.Size.Large}
                        styling={Image.Styling.Circle}
                      />
                    </div>
                    <Button
                      text={translate('photo_temp_uuid.action.upload')}
                      size={Button.Size.Small}
                      onClick={showPhotoFileUpload}
                      testId="business-profile-photo-upload-button"
                    />
                  </SeparatedList>
                </div>
              }
            />
            <input ref={photoFileInput} onChange={onFileInputChange} hidden type="file" />
            <input {...register(Field.PictureId)} hidden type="text" />
            <input {...register(Field.PictureUrl)} hidden type="text" />
          </>
          <InputText
            {...register(Field.BusinessAccountName, {
              required: true,
            })}
            styling={InputText.Styling.Wide}
            title={translate('business_account_name.title')}
            placeholder={translate('business_account_name.placeholder')}
            validation={renderBusinessNameValidation()}
          />
          <div>
            <InputText
              {...register(Field.ContactNumber, {
                required: true,
              })}
              styling={InputText.Styling.Wide}
              title={translate('phone_number.title')}
              placeholder={translate('phone_number.placeholder')}
              validation={
                <>
                  {renderValidation(validationMessage(Field.ContactNumber))}
                  {entityType === 'sole_trader' && (
                    <>
                      <Spacer size={Spacer.Size.XLarge} />
                      {renderIsPhoneNumberPersonalCheckbox()}
                    </>
                  )}
                </>
              }
            />
          </div>
          <div>
            <InputText
              {...register(Field.ContactEmail, {
                required: true,
              })}
              styling={InputText.Styling.Wide}
              title={translate('email.title')}
              placeholder={translate('email.placeholder')}
              validation={
                <>
                  {renderValidation(validationMessage(Field.ContactEmail))}
                  {entityType === 'sole_trader' && (
                    <>
                      <Spacer size={Spacer.Size.XLarge} />
                      {renderIsEmailPersonalCheckbox()}
                    </>
                  )}
                </>
              }
            />
          </div>
        </SeparatedList>
        {isPersonalContactInfoVisible && renderInformationModal()}
      </Section>
    </SeparatedList>
  )
}

export default BusinessProfile
